import get from 'lodash/get'
import { addWeeks, format, startOfWeek } from 'date-fns'
import { isShelflifeBasedApp, isPsmBasedApp } from '../../../van-shared/utils/utils'
export const IS_DEV = process.env.NODE_ENV === 'development'

export const byId = (collection, prop = 'id') => collection.reduce((
  index,
  element
) => {
  if (index) {
    index[element[prop]] = element
  }
  return index
}, {})

export const capitalize = string =>
  `${string[0].toUpperCase()}${string.slice(1)}`

const is = (type) => (input) => {
  return Object.prototype.toString.call(input) === `[object ${type}]`
}

export const isNumber = is('Number')
export const isString = is('String')

export const isNegativeValue = (input) => (input < 0)

// For use on e.g. onKeyDown on number fields that should
// not allow user to enter non-numeric input, like - or .
export const restrictNumberInput = event => {
  // Should do nothing if the default action has been cancelled
  if (event.defaultPrevented) return

  const unwantedKeys = '-eE.,'
  let isUnwantedKeyInput = false

  if (event.key !== undefined) {
    isUnwantedKeyInput = unwantedKeys.indexOf(event.key) >= 0
  }
  // KeyboardEvent.keyIdentifier or event.keyCode are not included
  // since this is just an assistive function to set user expectations
  // and key is covered in our main browsers for this use case.
  // https://caniuse.com/#feat=keyboardevent-key
  // If event.key is not present, we simple ignore.

  if (isUnwantedKeyInput) event.preventDefault()
  return !isUnwantedKeyInput
}

export const isFraction = (input) => {
  return input % 1 !== 0
}

export const promiseSerial = funcs => {
  return funcs.reduce((promise, fn) => (
    promise.then(results => fn().then(res => results.concat(res)))
  ), Promise.resolve([]))
}

export const sortProductByIndices = (serviceId, report, productsById) => {
  return Object.keys(report.stock)
    .sort((id1, id2) => {
      // Sort products by index from MD. Safe guard for when an index is not found.
      const a = productsById[id1]
      const b = productsById[id2]
      try {
        const i1 = a.services.find(s => s.id === serviceId).index
        const i2 = b.services.find(s => s.id === serviceId).index
        return i1 < i2 ? -1 : 1
      } catch (e) {
        return -1
      }
    })
}

export const sortProductSL = (serviceId, report, productsById) => {
  return Object.keys(report.stock)
    .filter(productId => {
      return !report.stock[productId].skipCount
    })
    .sort((id1, id2) => {
      // Sort products using product type name
      const a = productsById[id1]
      const b = productsById[id2]
      try {
        const i1 = a.productType
        const i2 = b.productType
        return i1 < i2 ? -1 : 1
      } catch (e) {
        return -1
      }
    })
}

/*
get date in a friendable format
*/
export const toFriendableDate = (date) => {
  return date.toLocaleString('en-NG', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  })
}

/**
 * Return the batchNumber for a given delimited batchId in the e.g
 * 'foo:bar:n:batchId' where batchId is the last part.
 *
 * @param {string} batchId
 */
export const batchNumberFromId = (batchId, delimiter = ':') => batchId.split(delimiter).reverse()[0]

/**
 * Calculates the progress percentage of a stock report.
 *
 * For example, if 3 out of 10 items are completed, it returns 0.3.
 * Returns 0 if the report is missing or invalid.
 *
 * @param {object} report - The report object containing progress data.
 * @returns {number} A decimal representing the completion percentage (0–1).
 */
export const calculateProgressPercentage = (report) => {
  if (!report) {
    return 0
  }

  const completedReport = get(report, 'progress.complete')
  const totalReport = get(report, 'progress.total')
  return completedReport / totalReport
}

/**
 * Checks which reporting period type applies to a program.
 *
 * Looks at the first reporting period to see if it is bimonthly, monthly, or weekly.
 *
 * @param {object} program - The program object with reporting configuration.
 * @returns {object} An object with boolean flags:
 *   - isBimonthly: true if every 2 months.
 *   - isMonthly: true if every 1 month.
 *   - isWeekly: true if every 1 week.
 */
export const getReportingPeriodicTypes = (program) => {
  const periodType = get(program, 'report.periods.0.periodType', {})

  if (!Object.keys(periodType).length) {
    return {}
  }

  const { unit, quantity } = periodType

  return {
    isBimonthly: unit === 'month' && quantity === 2,
    isMonthly: unit === 'month' && quantity === 1,
    isWeekly: unit === 'week' && quantity === 1
  }
}

/**
 * Generates weekly cycles for a given reporting year based on the program's entry start date.
 *
 * @param {object} program - Program object containing periods and offsets.
 * @returns {Array} Array of weekly cycle objects [{label, value}]
 */
export const getWeeklyCycle = (program, selectedYear) => {
  if (!program) {
    return []
  }

  const entryDate = get(program, 'periods.active.entryStartDate', new Date())
  const cycleYear = selectedYear || new Date(entryDate).getFullYear()

  // This will get you the first Monday of the year
  let weekStart = startOfWeek(new Date(cycleYear, 0, 1), { weekStartsOn: 1 })

  const cycles = []

  for (let i = 0; i < 52; i++) {
    const label = `Week ${i + 1}`
    const value = format(weekStart, 'yyyy-MM-dd')
    cycles.push({ label, value })
    weekStart = addWeeks(weekStart, 1)
  }

  return cycles
}

const BIMONTHLY_CYCLES = [{label: 'Jan - Feb', value: '03'}, {label: 'March - Apr', value: '05'}, {label: 'May - June', value: '07'}, {label: 'July - Aug', value: '09'}, {label: 'Sep - Oct', value: '11'}, {label: 'Nov - Dec', value: '01'}]
const QUARTERLY_CYCLES = [{label: 'Jan - March', value: '04'}, {label: 'Apr - June', value: '07'}, {label: 'July - Sep', value: '10'}, {label: 'Oct - Dec', value: '01'}]
const MONTHLY_CYCLES = [
  {label: 'Jan', value: '02'},
  {label: 'Feb', value: '03'},
  {label: 'March', value: '04'},
  {label: 'Apr', value: '05'},
  {label: 'May', value: '06'},
  {label: 'June', value: '07'},
  {label: 'July', value: '08'},
  {label: 'Aug', value: '09'},
  {label: 'Sep', value: '10'},
  {label: 'Oct', value: '11'},
  {label: 'Nov', value: '12'},
  {label: 'Dec', value: '01'}
]

export const getCycleOptions = (program, selectedYear) => {
  const { isBimonthly, isMonthly, isWeekly } = getReportingPeriodicTypes(program)

  if (isBimonthly) {
    return BIMONTHLY_CYCLES
  }

  if (isMonthly) {
    return MONTHLY_CYCLES
  }

  if (isWeekly) {
    return getWeeklyCycle(program, selectedYear)
  }

  return QUARTERLY_CYCLES
}

export { isShelflifeBasedApp, isPsmBasedApp }
